import { useCallback, useEffect, useRef, useState } from 'react';

export interface SearchableOption {
  id: string;
  name: string;
}

export interface UseSearchableOptionsResult<T> {
  options: T[];
  loading: boolean;
  error: string | null;
  setSearch: (value: string) => void;
  labelFor: (id: string) => string;
}

/**
 * Backs a `<SearchSelect>` with server-side (`?search=`) options instead of a
 * prefetched static list. When `active` flips true it fires an un-narrowed
 * fetch; keystrokes debounce for `debounceMs` before re-fetching. A label
 * cache keeps the picked option's name on the trigger even after a later
 * search narrows it out of the result set.
 *
 * Pass `active` from the host's open state (popover / modal) so we only query
 * while the dropdown is reachable; pass a constant `true` for always-visible
 * inline dropdowns.
 *
 * Sibling of `pages/invoices/.../useSearchableOptions` — kept as its own hook
 * so the invoices copy stays untouched.
 */
export function useSearchableOptions<T extends SearchableOption>(
  active: boolean,
  fetcher: (search?: string) => Promise<T[]>,
  debounceMs = 300
): UseSearchableOptionsResult<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const labelCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!active) {
      setSearch('');
      return;
    }

    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      fetcher(trimmed || undefined)
        .then((items) => {
          if (cancelled) return;
          setOptions(items);
          setError(null);
          for (const it of items) labelCache.current.set(it.id, it.name);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Failed to load options');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) {
      timer = setTimeout(fire, debounceMs);
    } else {
      fire();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, search, fetcher, debounceMs]);

  const labelFor = useCallback(
    (id: string) => labelCache.current.get(id) ?? '',
    []
  );

  return { options, loading, error, setSearch, labelFor };
}
